import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { InspectionOverviewDTO } from '../DTOs/InspectionOverviewDTO';
import { EmptyStateComponent } from '../Shared/Components/empty-state/empty-state.component';
import { AuthService } from '../Shared/Services/authentication.service';
import { MasterDataService } from '../Services/master-data.service';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, EmptyStateComponent],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css']
})
export class HomeComponent implements OnInit, OnDestroy {
  public loading = false;
  public upcomingInspections: InspectionOverviewDTO[] = [];

  private readonly destroy$ = new Subject<void>();

  constructor(
    public auth: AuthService,
    private readonly masterDataService: MasterDataService,
    private readonly router: Router,
  ) { }

  ngOnInit(): void {
    this.loadUpcomingInspections();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public openInspectionOverview(): void {
    this.router.navigate(['/inspection-overview']);
  }

  public openInspection(inspection: InspectionOverviewDTO): void {
    this.router.navigate(['/inspection-detail', inspection.inspectionId]);
  }

  public executeInspection(inspection: InspectionOverviewDTO): void {
    this.router.navigate(['/executeInspection', inspection.inspectionId]);
  }

  public statusLabel(inspection: InspectionOverviewDTO): string {
    if (inspection.inspectionStatus) {
      return inspection.inspectionStatus;
    }

    switch (inspection.inspectionStatusId) {
      case 1: return 'Created';
      case 2: return 'Started';
      case 3: return 'Passed';
      case 4: return 'Failed';
      default: return 'Unknown';
    }
  }

  public statusClass(inspection: InspectionOverviewDTO): string {
    switch (inspection.inspectionStatusId) {
      case 1: return 'status-chip--created';
      case 2: return 'status-chip--started';
      case 3: return 'status-chip--passed';
      case 4: return 'status-chip--failed';
      default: return '';
    }
  }

  public isStarted(inspection: InspectionOverviewDTO): boolean {
    return inspection.inspectionStatusId > 1;
  }

  private loadUpcomingInspections(): void {
    this.loading = true;

    this.masterDataService.getInspectionOverview()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (rows) => {
          this.upcomingInspections = this.getUpcomingInspections(rows ?? []);
          this.loading = false;
        },
        error: () => {
          this.upcomingInspections = [];
          this.loading = false;
        }
      });
  }

  private getUpcomingInspections(rows: InspectionOverviewDTO[]): InspectionOverviewDTO[] {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return rows
      .filter(row => row.inspectionStatusId === 1 || row.inspectionStatusId === 2)
      .map(row => ({ row, date: this.parseInspectionDate(row.inspectionDate) }))
      .filter(entry => entry.date !== null && entry.date >= today)
      .sort((left, right) => {
        const dateDiff = left.date!.getTime() - right.date!.getTime();
        if (dateDiff !== 0) {
          return dateDiff;
        }

        return left.row.inspectionId - right.row.inspectionId;
      })
      .slice(0, 6)
      .map(entry => entry.row);
  }

  private parseInspectionDate(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
}
